import React, { useState, useEffect, useCallback } from 'react'
import {
  Clock,
  Plus,
  Trash2,
  Save,
  AlertCircle,
  Check,
  Calendar,
  Globe,
} from 'lucide-react'
import { useAuth } from '@/lib/auth/context'
import {
  useTeamAvailability,
  useAvailabilityOverrides,
  useAvailabilityMutations,
} from '@/lib/data/hooks/useAvailability'
import { TeamAvailability, AvailabilityOverride, OverrideType } from '@/types/calendar'

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
]

const TIME_OPTIONS = Array.from({ length: 24 * 2 }, (_, i) => {
  const hour = Math.floor(i / 2)
  const minute = i % 2 === 0 ? '00' : '30'
  const time = `${hour.toString().padStart(2, '0')}:${minute}`
  const label = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return { value: time, label }
})

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Dubai',
  'Australia/Sydney',
  'UTC',
]

export function AvailabilitySettings() {
  const { adminUser } = useAuth()
  const { availability, isLoading: loadingAvailability, refetch } = useTeamAvailability(adminUser?.id)

  // Get overrides for next 90 days
  const today = new Date()
  const startDate = today.toISOString().split('T')[0]
  const endDate = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

  const { overrides, isLoading: loadingOverrides, refetch: refetchOverrides } = useAvailabilityOverrides(adminUser?.id, startDate, endDate)
  const { setAvailability, setDefaultAvailability, removeAvailability, addOverride, removeOverride, isLoading: saving } = useAvailabilityMutations()

  const [localAvailability, setLocalAvailability] = useState<Record<number, { enabled: boolean; startTime: string; endTime: string }>>({})
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone)
  const [hasChanges, setHasChanges] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Override form state
  const [showOverrideForm, setShowOverrideForm] = useState(false)
  const [overrideDate, setOverrideDate] = useState('')
  const [overrideType, setOverrideType] = useState<OverrideType>('unavailable')
  const [overrideStartTime, setOverrideStartTime] = useState('09:00')
  const [overrideEndTime, setOverrideEndTime] = useState('17:00')
  const [overrideReason, setOverrideReason] = useState('')

  // Initialize local state from fetched availability
  useEffect(() => {
    if (availability.length > 0) {
      const byDay: Record<number, { enabled: boolean; startTime: string; endTime: string }> = {}

      // Initialize all days as disabled
      DAYS_OF_WEEK.forEach(day => {
        byDay[day.value] = { enabled: false, startTime: '09:00', endTime: '17:00' }
      })

      // Update with actual availability
      availability.forEach(a => {
        byDay[a.dayOfWeek] = {
          enabled: a.isAvailable,
          startTime: a.startTime,
          endTime: a.endTime,
        }
        if (a.timezone) {
          setTimezone(a.timezone)
        }
      })

      setLocalAvailability(byDay)
    } else {
      // Default: Mon-Fri 9-5
      const byDay: Record<number, { enabled: boolean; startTime: string; endTime: string }> = {}
      DAYS_OF_WEEK.forEach(day => {
        byDay[day.value] = {
          enabled: day.value >= 1 && day.value <= 5, // Mon-Fri
          startTime: '09:00',
          endTime: '17:00',
        }
      })
      setLocalAvailability(byDay)
    }
  }, [availability])

  const handleDayToggle = (dayOfWeek: number) => {
    setLocalAvailability(prev => ({
      ...prev,
      [dayOfWeek]: { ...prev[dayOfWeek], enabled: !prev[dayOfWeek]?.enabled },
    }))
    setHasChanges(true)
  }

  const handleTimeChange = (dayOfWeek: number, field: 'startTime' | 'endTime', value: string) => {
    setLocalAvailability(prev => ({
      ...prev,
      [dayOfWeek]: { ...prev[dayOfWeek], [field]: value },
    }))
    setHasChanges(true)
  }

  const handleSave = async () => {
    setSaveSuccess(false)

    // Save each day's availability
    for (const day of DAYS_OF_WEEK) {
      const dayAvail = localAvailability[day.value]
      if (dayAvail) {
        await setAvailability({
          dayOfWeek: day.value,
          startTime: dayAvail.startTime,
          endTime: dayAvail.endTime,
          isAvailable: dayAvail.enabled,
          timezone,
        })
      }
    }

    setHasChanges(false)
    setSaveSuccess(true)
    refetch()

    setTimeout(() => setSaveSuccess(false), 3000)
  }

  const handleSetDefaults = async () => {
    await setDefaultAvailability({ timezone })
    refetch()
    setHasChanges(false)
  }

  const handleAddOverride = async () => {
    if (!overrideDate) return

    await addOverride({
      overrideDate,
      overrideType,
      startTime: overrideType === 'custom' ? overrideStartTime : undefined,
      endTime: overrideType === 'custom' ? overrideEndTime : undefined,
      reason: overrideReason || undefined,
    })

    // Reset form
    setShowOverrideForm(false)
    setOverrideDate('')
    setOverrideType('unavailable')
    setOverrideStartTime('09:00')
    setOverrideEndTime('17:00')
    setOverrideReason('')
    refetchOverrides()
  }

  const handleRemoveOverride = async (overrideId: string) => {
    await removeOverride(overrideId)
    refetchOverrides()
  }

  const formatOverrideDate = (dateStr: string) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (loadingAvailability) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-[var(--text-main)] mb-2">
          Your Availability
        </h3>
        <p className="text-[var(--text-muted)] text-sm">
          Set your regular working hours for meeting scheduling
        </p>
      </div>

      {/* Timezone Selector */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 text-[var(--text-muted)]">
          <Globe size={16} />
          <span className="text-sm font-medium">Timezone</span>
        </div>
        <select
          value={timezone}
          onChange={(e) => {
            setTimezone(e.target.value)
            setHasChanges(true)
          }}
          className="px-3 py-2 bg-[var(--bg-main)] border border-[var(--border)] rounded-lg text-[var(--text-main)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/50"
        >
          {TIMEZONES.map(tz => (
            <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      {/* Weekly Schedule */}
      <div className="bg-[var(--bg-main)] rounded-xl border border-[var(--border)] overflow-hidden">
        <div className="p-4 border-b border-[var(--border)] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock size={18} className="text-[var(--primary)]" />
            <span className="font-medium text-[var(--text-main)]">Weekly Schedule</span>
          </div>
          <button
            onClick={handleSetDefaults}
            disabled={saving}
            className="text-sm text-[var(--primary)] hover:underline disabled:opacity-50"
          >
            Reset to defaults (Mon-Fri 9-5)
          </button>
        </div>

        <div className="divide-y divide-[var(--border)]">
          {DAYS_OF_WEEK.map(day => {
            const dayAvail = localAvailability[day.value] || { enabled: false, startTime: '09:00', endTime: '17:00' }
            return (
              <div
                key={day.value}
                className={`flex items-center gap-4 p-4 transition-colors ${
                  dayAvail.enabled ? '' : 'opacity-50'
                }`}
              >
                {/* Day Toggle */}
                <button
                  onClick={() => handleDayToggle(day.value)}
                  className={`w-20 text-left font-medium transition-colors ${
                    dayAvail.enabled ? 'text-[var(--text-main)]' : 'text-[var(--text-muted)]'
                  }`}
                >
                  {day.label}
                </button>

                {/* Toggle Switch */}
                <button
                  onClick={() => handleDayToggle(day.value)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    dayAvail.enabled ? 'bg-[var(--primary)]' : 'bg-[var(--border)]'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      dayAvail.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>

                {/* Time Range */}
                {dayAvail.enabled && (
                  <div className="flex items-center gap-2 ml-4">
                    <select
                      value={dayAvail.startTime}
                      onChange={(e) => handleTimeChange(day.value, 'startTime', e.target.value)}
                      className="px-2 py-1.5 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/50"
                    >
                      {TIME_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <span className="text-[var(--text-muted)]">to</span>
                    <select
                      value={dayAvail.endTime}
                      onChange={(e) => handleTimeChange(day.value, 'endTime', e.target.value)}
                      className="px-2 py-1.5 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/50"
                    >
                      {TIME_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {!dayAvail.enabled && (
                  <span className="text-sm text-[var(--text-muted)] ml-4">Unavailable</span>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Save Button */}
      {hasChanges && (
        <div className="flex items-center gap-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : (
              <Save size={16} />
            )}
            Save Changes
          </button>
          {saveSuccess && (
            <span className="flex items-center gap-1 text-green-500 text-sm">
              <Check size={16} />
              Saved!
            </span>
          )}
        </div>
      )}

      {/* Date Overrides */}
      <div className="pt-6 border-t border-[var(--border)]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-medium text-[var(--text-main)]">Date Overrides</h4>
            <p className="text-sm text-[var(--text-muted)]">
              Block specific dates or set custom hours
            </p>
          </div>
          <button
            onClick={() => setShowOverrideForm(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-[var(--primary)] border border-[var(--primary)] rounded-lg hover:bg-[var(--primary)]/10 transition-colors"
          >
            <Plus size={14} />
            Add Override
          </button>
        </div>

        {/* Override Form */}
        {showOverrideForm && (
          <div className="bg-[var(--bg-main)] rounded-xl border border-[var(--border)] p-4 mb-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-main)] mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={overrideDate}
                  onChange={(e) => setOverrideDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-main)] mb-1">
                  Type
                </label>
                <select
                  value={overrideType}
                  onChange={(e) => setOverrideType(e.target.value as OverrideType)}
                  className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/50"
                >
                  <option value="unavailable">Unavailable (full day)</option>
                  <option value="custom">Custom hours</option>
                </select>
              </div>
            </div>

            {overrideType === 'custom' && (
              <div className="flex items-center gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-main)] mb-1">
                    From
                  </label>
                  <select
                    value={overrideStartTime}
                    onChange={(e) => setOverrideStartTime(e.target.value)}
                    className="px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/50"
                  >
                    {TIME_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-main)] mb-1">
                    To
                  </label>
                  <select
                    value={overrideEndTime}
                    onChange={(e) => setOverrideEndTime(e.target.value)}
                    className="px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/50"
                  >
                    {TIME_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[var(--text-main)] mb-1">
                Reason (optional)
              </label>
              <input
                type="text"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="e.g., Holiday, Doctor's appointment"
                className="w-full px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[var(--text-main)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/50"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleAddOverride}
                disabled={!overrideDate || saving}
                className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                Add Override
              </button>
              <button
                onClick={() => setShowOverrideForm(false)}
                className="px-4 py-2 text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Existing Overrides */}
        {loadingOverrides ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--primary)] mx-auto" />
          </div>
        ) : overrides.length === 0 ? (
          <div className="text-center py-6 text-[var(--text-muted)]">
            <Calendar size={24} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No date overrides set</p>
          </div>
        ) : (
          <div className="space-y-2">
            {overrides.map(override => (
              <div
                key={override.id}
                className="flex items-center justify-between p-3 bg-[var(--bg-main)] rounded-lg border border-[var(--border)]"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${
                    override.overrideType === 'unavailable' ? 'bg-red-500' : 'bg-yellow-500'
                  }`} />
                  <div>
                    <p className="font-medium text-[var(--text-main)]">
                      {formatOverrideDate(override.overrideDate)}
                    </p>
                    <p className="text-sm text-[var(--text-muted)]">
                      {override.overrideType === 'unavailable'
                        ? 'Unavailable'
                        : `${override.startTime} - ${override.endTime}`}
                      {override.reason && ` - ${override.reason}`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveOverride(override.id)}
                  className="p-2 text-[var(--text-muted)] hover:text-red-500 transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
